import { useMutation } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useAxios } from './use-axios';

const API_BASE_URL = '/contacts-extraction';

interface ApiError {
  error?: string;
  details?: Array<{ message: string }>;
}

function getErrorMessage(error: unknown): string {
  if (isAxiosError<ApiError>(error)) {
    if (error.response?.data?.details?.length) {
      return error.response.data.details.map((d) => d.message).join(', ');
    }

    if (error.response?.data?.error) {
      return error.response.data.error;
    }

    const status = error.response?.status;
    if (status === 400) {
      return 'Invalid request. Please double-check your input.';
    }
    if (status === 401 || status === 403) {
      return 'You are not authorized to perform this action.';
    }
    if (status === 404) {
      return 'The requested resource was not found.';
    }
    if (status === 500) {
      return 'Contact extraction service returned an error. Please try again.';
    }
    if (error.message === 'Network Error') {
      return 'Unable to connect to the server. Check your internet connection.';
    }
  }

  return 'An unexpected error occurred. Please try again.';
}

export interface ContactChange {
  field: string;
  displayField: string;
  value: string;
  confidence: 'high' | 'medium' | 'low';
}

export interface ContactsExtractionResponse {
  isVerificationTask: boolean;
  hasChanges: boolean;
  verifierType: 'registrant' | 'verification' | null;
  domains: string[];
  isCorrect: boolean;
  changes: ContactChange[];
  aiReason: string | null;
}

interface ExtractFromTaskPayload {
  taskContent: string;
}

/**
 * Hook to extract contact changes from a verification task using AI.
 * Uses text parsing for domains (predictable format) and AI for contact field changes.
 */
export function useExtractContactChanges() {
  const axios = useAxios();

  return useMutation({
    mutationFn: async (payload: ExtractFromTaskPayload) => {
      try {
        const { data } = await axios.post<ContactsExtractionResponse>(
          `${API_BASE_URL}/from-task`,
          payload,
        );
        return data;
      } catch (error) {
        throw new Error(getErrorMessage(error));
      }
    },
  });
}
